import { Flex, Spacing, Text } from '@billy/design-system'
import isNil from 'lodash/isNil'
import React, { ReactElement, useCallback } from 'react'
import { useNavigate } from 'react-router'

import { getBankAccountTotalAmount } from '@features/bankAccounts/utils/getBankAccountTotalAmount'
import { useBankTransactionsAccess } from '@features/bankTransactions/hooks/useBankTransactionsAccess'

import { EmberRoute } from '../../../../../../../../enums/emberRoute'
import { useEmberRouteToUrlGenerator } from '../../../../../../../../hooks/routing/useEmberRouteToUrlGenerator'
import { useEmberTransitionTo } from '../../../../../../../../hooks/routing/useEmberTransitionTo'
import { useOrganizationViewUrl } from '../../../../../../../../hooks/routing/useOrganizationViewUrl'
import { useLocale } from '../../../../../../../../hooks/useLocale'
import { reactRoute } from '../../../../../../../../utils/routing/reactRoute'
import { CardActionType } from '../../enums/cardActionType'
import { SyncStatus } from '../../enums/syncStatus'
import { BaseAccount } from '../../types/baseAccount'
import { AccountSyncStatus } from './elements/AccountSyncStatus/AccountSyncStatus'
import { BankAccountTitle } from './elements/BankAccountTitle'
import { BankConnectionDetails } from './elements/BankConnectionDetails'
import { CardActionsButton } from './elements/CardActionsButton'
import * as Styled from './styles'

interface BaseAccountCardProps {
  account: BaseAccount
  actionItems?: CardActionType[]
  bankLogo?: string
  bankName: string
  syncStatus: SyncStatus
  syncDate?: string
  isRenewing?: boolean
  onRenew?: () => void
}

export const BaseAccountCard = ({
  account,
  actionItems = [],
  bankLogo,
  bankName,
  syncStatus,
  syncDate,
  isRenewing,
  onRenew,
}: BaseAccountCardProps): ReactElement => {
  const navigate = useNavigate()
  const { generateUrl } = useEmberRouteToUrlGenerator()
  const { transitionTo } = useEmberTransitionTo()
  const { code } = useLocale()
  const { isTransactionsDefaultView } = useBankTransactionsAccess()

  const { accountNo, accountName, bankAccountId, customAccountName, currency, externalAccountId, iban, totalAmount } =
    account
  const isNegativeAmount = totalAmount && totalAmount < 0

  const { url: bankTransactionsUrl } = useOrganizationViewUrl(
    reactRoute.organization.bank.getBankTransactionsRoute(),
    externalAccountId ? { accountId: externalAccountId } : undefined,
  )

  const handleCardClick = useCallback(() => {
    if (isTransactionsDefaultView && bankTransactionsUrl) {
      navigate(bankTransactionsUrl)
      return
    }

    if (generateUrl) {
      const url = generateUrl(EmberRoute.Bank, externalAccountId)
      transitionTo(url)
    }
  }, [bankTransactionsUrl, externalAccountId, generateUrl, isTransactionsDefaultView, navigate, transitionTo])

  const preventPropagation = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation()
  }

  return (
    <Styled.BaseAccountCardWrapper onClick={handleCardClick}>
      <Styled.TopSection>
        <Flex flexDirection="column" gap={Spacing.S4}>
          <BankAccountTitle bankName={bankName} bankLogo={bankLogo} />
          <BankConnectionDetails
            accountName={accountName}
            customAccountName={customAccountName}
            iban={iban}
            accountNo={accountNo}
          />
        </Flex>

        <Styled.InteractiveElement onClick={preventPropagation}>
          <CardActionsButton actions={actionItems} accountId={externalAccountId} bankAccountId={bankAccountId} />
        </Styled.InteractiveElement>
      </Styled.TopSection>

      <Flex flexDirection="column" gap={Spacing.S6}>
        {!isNil(totalAmount) && currency && (
          <Text variant="h3" colorVariant={isNegativeAmount ? 'danger' : 'primary'}>
            {getBankAccountTotalAmount({ amount: totalAmount, currency, locale: code })}
          </Text>
        )}

        <Styled.InteractiveElement onClick={preventPropagation}>
          <AccountSyncStatus syncStatus={syncStatus} syncDate={syncDate} isRenewing={isRenewing} onRenew={onRenew} />
        </Styled.InteractiveElement>
      </Flex>
    </Styled.BaseAccountCardWrapper>
  )
}
